import { useState } from 'react';
import { AsyncBoundary, Card, Notice, useToast } from '@kollez/design-system';
import {
  useTenantDomains,
  useAddTenantDomain,
  useVerifyTenantDomain,
  useRemoveTenantDomain,
  type TenantDomain,
} from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /tpo/settings/domains — verified email-domain management for Tier-2a auto-approval.
 *
 * A student who signs up with an email that matches one of these verified domains gets
 * auto-added to the tenant's roster (subject to the auto-approved review queue at
 * /tpo/memberships/auto-approved). Domain verification is a DNS TXT record proof.
 *
 * Endpoints all mounted on tpo.routes.ts, no consumer previously:
 *   GET    /tpo/tenant/domains
 *   POST   /tpo/tenant/domains            — mint token + register as pending
 *   POST   /tpo/tenant/domains/:d/verify  — DNS TXT re-check
 *   DELETE /tpo/tenant/domains/:d         — remove
 */

export function TpoDomainsPage() {
  const list = useTenantDomains();
  const add = useAddTenantDomain();
  const verify = useVerifyTenantDomain();
  const remove = useRemoveTenantDomain();
  const toast = useToast();
  const [newDomain, setNewDomain] = useState('');
  const [pendingInstruction, setPendingInstruction] = useState<{
    domain: string;
    txtRecordName: string;
    txtRecordValue: string;
  } | null>(null);
  const [busyDomain, setBusyDomain] = useState<string | null>(null);

  const domains = list.data?.domains ?? [];
  const verifiedCount = domains.filter((d) => d.status === 'verified').length;
  const pendingCount = domains.filter((d) => d.status !== 'verified').length;

  async function doAdd() {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    try {
      const res = await add.mutateAsync({ domain });
      setPendingInstruction({
        domain: res.domain,
        txtRecordName: res.txtRecordName,
        txtRecordValue: res.txtRecordValue,
      });
      setNewDomain('');
      toast.show({ message: `Registered ${res.domain}. Publish the TXT record, then verify.` });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Add failed.' });
    }
  }

  async function doVerify(domain: string) {
    setBusyDomain(domain);
    try {
      const res = await verify.mutateAsync({ domain });
      if (res.status === 'verified') {
        toast.show({ message: `Verified ${domain}.` });
        if (pendingInstruction?.domain === domain) setPendingInstruction(null);
      } else {
        toast.show({
          tone: 'warning',
          message: res.reason ?? `Still not verified (attempt ${res.checkAttempts}).`,
        });
      }
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Verify failed.' });
    } finally {
      setBusyDomain(null);
    }
  }

  async function doRemove(domain: string) {
    if (!window.confirm(`Remove ${domain}? Future signups from this domain will no longer auto-approve.`)) return;
    setBusyDomain(domain);
    try {
      const res = await remove.mutateAsync({ domain });
      toast.show({ message: res.removed ? `Removed ${domain}.` : `${domain} was not registered.` });
      if (pendingInstruction?.domain === domain) setPendingInstruction(null);
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Remove failed.' });
    } finally {
      setBusyDomain(null);
    }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Settings · Verified email domains</p>
          <h1 className="h-page">Email-domain auto-approval</h1>
          <p className="subhead">
            Add your college's email domains (e.g. <code>git.edu.in</code>). Students who sign up
            with a matching email are auto-verified onto your roster. Spot-audit them at{' '}
            <a className="link" href="/tpo/memberships/auto-approved">
              Recent auto-approvals
            </a>
            .
          </p>
        </div>
      </div>

      <Notice tone="info">
        Verification proves you control the domain. Add the domain here, publish the DNS TXT
        record we generate, then click Verify. Verification is one-time; you can re-verify later
        if the record changes.
      </Notice>

      <Card padding="md">
        <div className="flex items-end gap-2 flex-wrap">
          <label className="flex flex-col text-sm grow">
            <span className="cell-sub">Add a domain</span>
            <input
              className="input mt-1"
              placeholder="e.g. git.edu.in"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void doAdd();
              }}
            />
          </label>
          <button
            type="button"
            className="btn btn-primary"
            disabled={add.isPending || !newDomain.trim()}
            onClick={() => void doAdd()}
          >
            {add.isPending ? 'Adding…' : 'Add domain'}
          </button>
        </div>
        <div className="cell-sub mt-2">
          {verifiedCount} verified · {pendingCount} pending
        </div>
      </Card>

      {pendingInstruction && (
        <Card padding="md">
          <div className="cell-title">Publish this TXT record for {pendingInstruction.domain}</div>
          <div className="cell-sub mt-1">
            Add the following TXT record at your DNS provider, then click Verify on the row below.
            Some providers take a few minutes to propagate.
          </div>
          <dl className="mt-3 grid gap-2 text-sm">
            <div className="flex gap-3">
              <dt className="cell-sub w-24">Name</dt>
              <dd>
                <code>{pendingInstruction.txtRecordName}</code>
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="cell-sub w-24">Value</dt>
              <dd>
                <code>{pendingInstruction.txtRecordValue}</code>
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="cell-sub w-24">Type</dt>
              <dd>
                <code>TXT</code>
              </dd>
            </div>
          </dl>
        </Card>
      )}

      <Card padding="none">
        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && domains.length === 0}
          emptyTitle="No domains registered yet"
          emptyHint="Add your first domain above. Once verified, matching signups auto-approve."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Status</th>
                  <th className="hide-mobile">Added</th>
                  <th className="hide-mobile">Last checked</th>
                  <th className="r">Actions</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((d) => (
                  <DomainRow
                    key={d.domain}
                    d={d}
                    busy={busyDomain === d.domain}
                    onVerify={() => void doVerify(d.domain)}
                    onRemove={() => void doRemove(d.domain)}
                    onShowInstructions={() =>
                      setPendingInstruction({
                        domain: d.domain,
                        txtRecordName: d.domain,
                        txtRecordValue: `kollez-verify=${d.token}`,
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </Card>
    </div>
  );
}

function DomainRow({
  d,
  busy,
  onVerify,
  onRemove,
  onShowInstructions,
}: {
  d: TenantDomain;
  busy: boolean;
  onVerify: () => void;
  onRemove: () => void;
  onShowInstructions: () => void;
}) {
  const tone =
    d.status === 'verified' ? 'badge-success' : d.status === 'failed' ? 'badge-warning' : 'badge';
  return (
    <tr>
      <td>
        <div className="cell-title">{d.domain}</div>
      </td>
      <td>
        <span className={tone}>{d.status}</span>
        {d.status !== 'verified' && d.checkAttempts > 0 && (
          <span className="cell-sub ml-2">· {d.checkAttempts} attempts</span>
        )}
      </td>
      <td className="hide-mobile cell-sub">
        {d.addedAt ? formatIndianDate(new Date(d.addedAt)) : '—'}
      </td>
      <td className="hide-mobile cell-sub">
        {d.lastCheckedAt ? formatIndianDate(new Date(d.lastCheckedAt)) : '—'}
      </td>
      <td className="r">
        <div className="flex justify-end gap-1 flex-wrap">
          {d.status !== 'verified' && (
            <>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={onShowInstructions}
                title="Show the TXT record to publish"
              >
                DNS record
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={onVerify}
              >
                {busy ? 'Checking…' : 'Verify'}
              </button>
            </>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={onRemove}
          >
            Remove
          </button>
        </div>
      </td>
    </tr>
  );
}
